import { createContext, ReactNode, useContext, useEffect, useState } from 'react'
import {
  CalculationMode,
  EmissionFactorMode,
  EnergyConsumptionCalculationBasis,
  EnergyPriceType,
  PowerUnit,
  SensorAggregateField,
  SensorAggregateStatistic,
  StartDateMode
} from '../enums'
import {
  HierarchySettings,
  ProjectAndDeviceStatsSettings,
  ProjectSettings,
  SensorSettings,
  ValveSensorField,
  ValveSensorHumidityField,
  ValveSensorPressureField,
  ValveSensorStatistic,
  ValveSensorTemperatureField
} from '../types'
import {
  CurrentField,
  FrequencyField,
  PowerFactorField,
  PowerField,
  VoltageField
} from '../enums/SensorAggregateField'
import {
  CurrentStatistic,
  FrequencyStatistic,
  PowerFactorStatistic,
  PowerStatistic,
  VoltageStatistic
} from '../enums/SensorAggregateStatistic'
import { DeviceManagerSettings } from '../hooks/useDeviceManager'
import { AssetScrollerSettings } from '../hooks/useAssetScrollerSettings'
import ValveSensorTemperatureChartCard from '../components/ValveSensorTemperatureChartCard'
import ValveSensorPositionField from '../types/ValveSensorPositionField'

interface PowerStatsSettings {
  statistics: PowerStatistic[]
  fields: PowerField[]
  showTotalMaxPower: boolean
  showPhaseMaxPower: boolean
  zoomIn: boolean
}
interface VoltageStatsSettings {
  statistics: VoltageStatistic[]
  fields: VoltageField[]
  zoomIn: boolean
}

interface CurrentStatsSettings {
  statistics: CurrentStatistic[]
  fields: CurrentField[]
  showRatedCurrent: boolean
  zoomIn: boolean
}

interface FrequencyStatsSettings {
  statistics: FrequencyStatistic[]
  fields: FrequencyField[]
}

interface PowerFactorStatsSettings {
  statistics: PowerFactorStatistic[]
  fields: PowerFactorField[]
}

interface EnergyConsumptionStatsSettings {
  calculationMode: CalculationMode
  calculationBasis: EnergyConsumptionCalculationBasis
  calculationStart: StartDateMode
}

interface EnergyCostStatsSettings {
  calculationMode: CalculationMode
  calculationBasis: EnergyConsumptionCalculationBasis
  calculationStart: StartDateMode
}

interface EnergyEmissionStatsSettings {
  calculationMode: CalculationMode
  calculationBasis: EnergyConsumptionCalculationBasis
  calculationStart: StartDateMode
}

interface GeneratorSettings {
  powerUnit: PowerUnit
  additionalLoadUnit: PowerUnit
}

type ComponentSettings = {
  AssetScroller?: AssetScrollerSettings
  DeviceManager?: DeviceManagerSettings
  ValveSensorTemperatureChart?: ValveSensorTemperatureChartSettings
  ValveSensorHumidityChart?: ValveSensorHumidityChartSettings
  ValveSensorPressureChart?: ValveSensorPressureChartSettings
  ValveSensorPositionChart?: ValveSensorPositionChartSettings
  ValveStats?: ValveStatsSettings
}

type ValveSensorTemperatureChartSettings = {
  fields: ValveSensorTemperatureField[]
  statistics: ValveSensorStatistic[]
  autoScaleYAxis: boolean
  showLegends: boolean
}

type ValveSensorPressureChartSettings = {
  fields: ValveSensorPressureField[]
  statistics: ValveSensorStatistic[]
  autoScaleYAxis: boolean
  showLegends: boolean
}

type ValveSensorHumidityChartSettings = {
  fields: ValveSensorHumidityField[]
  statistics: ValveSensorStatistic[]
  autoScaleYAxis: boolean
  showLegends: boolean
}

type ValveSensorPositionChartSettings = {
  fields: ValveSensorPositionField[]
  statistics: ValveSensorStatistic[]
  autoScaleYAxis: boolean
  showLegends: boolean
  absolutePosition: boolean
}

type ValveStatsSettings = {
  fields: ValveSensorField[]
  statistics: ValveSensorStatistic[]
}

interface Settings {
  energyPriceType: EnergyPriceType
  energyPrice: number
  emissionFactorMode: EmissionFactorMode
  emissionFactorValue: number

  /**
   * The settings for the power stats.
   */
  powerStatsSettings?: PowerStatsSettings

  /**
   * The settings for the voltage stats.
   */
  voltageStatsSettings?: VoltageStatsSettings

  /**
   * The settings for the current stats.
   */
  currentStatsSettings?: CurrentStatsSettings

  /**
   * The settings for the frequency stats.
   */
  frequencyStatsSettings?: FrequencyStatsSettings

  /**
   * The settings for the power factor stats.
   */
  powerFactorStatsSettings?: PowerFactorStatsSettings

  /**
   * The settings for the energy consumption stats.
   */
  energyConsumptionStatsSettings?: EnergyConsumptionStatsSettings

  /**
   * The settings for the energy cost stats.
   */
  energyCostStatsSettings?: EnergyCostStatsSettings

  /**
   * The settings for the energy emission stats.
   */
  energyEmissionStatsSettings?: EnergyEmissionStatsSettings

  projects?: Record<number, ProjectSettings>
  sensorSettings?: SensorSettings
  projectStatsSettings?: ProjectAndDeviceStatsSettings
  deviceStatsSettings?: ProjectAndDeviceStatsSettings
  hierarchySettings?: HierarchySettings
  generator: GeneratorSettings
  components?: ComponentSettings
}

interface SettingsContextType {
  settings: Settings
  powerStatsSettings: PowerStatsSettings
  voltageStatsSettings: VoltageStatsSettings
  currentStatsSettings: CurrentStatsSettings
  frequencyStatsSettings: FrequencyStatsSettings
  powerFactorStatsSettings: PowerFactorStatsSettings

  /**
   * The energy consumption stats settings.
   */
  energyConsumptionStatsSettings: EnergyConsumptionStatsSettings

  /**
   * The energy cost stats settings.
   */
  energyCostStatsSettings: EnergyCostStatsSettings

  /**
   * The energy emission stats settings.
   */
  energyEmissionStatsSettings: EnergyEmissionStatsSettings

  setSettings: (settings: Settings) => void
  setPowerStatsSettings: (powerChartSettings: PowerStatsSettings) => void
  setVoltageStatsSettings: (voltageStatsSettings: VoltageStatsSettings) => void
  setCurrentStatsSettings: (currentStatsSettings: CurrentStatsSettings) => void
  setFrequencyStatsSettings: (frequencyStatsSettings: FrequencyStatsSettings) => void
  setPowerFactorStatsSettings: (powerFactorStatsSettings: PowerFactorStatsSettings) => void

  /**
   * Set the energy stats settings.
   */
  setEnergyConsumptionStatsSettings: (settings: EnergyConsumptionStatsSettings) => void

  components?: ComponentSettings
}

const SettingsContext = createContext<SettingsContextType>({
  settings: {
    energyPriceType: EnergyPriceType.SPOT,
    energyPrice: 0,
    emissionFactorMode: EmissionFactorMode.REALTIME,
    emissionFactorValue: 0,
    generator: {
      powerUnit: PowerUnit.KILOVOLT_AMPERE,
      additionalLoadUnit: PowerUnit.KILOWATT
    }
  },
  powerStatsSettings: {
    statistics: [SensorAggregateStatistic.AVG],
    fields: [
      SensorAggregateField.POWER,
      SensorAggregateField.POWER_1,
      SensorAggregateField.POWER_2,
      SensorAggregateField.POWER_3
    ],
    showTotalMaxPower: false,
    showPhaseMaxPower: true,
    zoomIn: false
  },
  voltageStatsSettings: {
    statistics: [SensorAggregateStatistic.AVG],
    fields: [
      SensorAggregateField.VOLTAGE_1,
      SensorAggregateField.VOLTAGE_2,
      SensorAggregateField.VOLTAGE_3
    ],
    zoomIn: true
  },
  currentStatsSettings: {
    statistics: [SensorAggregateStatistic.AVG],
    fields: [
      SensorAggregateField.CURRENT_1,
      SensorAggregateField.CURRENT_2,
      SensorAggregateField.CURRENT_3
    ],
    showRatedCurrent: false,
    zoomIn: true
  },
  frequencyStatsSettings: {
    statistics: [SensorAggregateStatistic.AVG],
    fields: [
      SensorAggregateField.FREQUENCY_FACTOR_1,
      SensorAggregateField.FREQUENCY_FACTOR_2,
      SensorAggregateField.FREQUENCY_FACTOR_3
    ]
  },
  powerFactorStatsSettings: {
    statistics: [SensorAggregateStatistic.AVG],
    fields: [
      SensorAggregateField.POWER_FACTOR_1,
      SensorAggregateField.POWER_FACTOR_2,
      SensorAggregateField.POWER_FACTOR_3
    ]
  },

  energyConsumptionStatsSettings: {
    calculationMode: CalculationMode.CUMULATIVE,
    calculationBasis: EnergyConsumptionCalculationBasis.ENERGY_COUNTER,
    calculationStart: StartDateMode.START_OF_SELECTED_DATE_RANGE
  },
  energyCostStatsSettings: {
    calculationMode: CalculationMode.CUMULATIVE,
    calculationBasis: EnergyConsumptionCalculationBasis.ENERGY_COUNTER,
    calculationStart: StartDateMode.START_OF_SELECTED_DATE_RANGE
  },
  energyEmissionStatsSettings: {
    calculationMode: CalculationMode.CUMULATIVE,
    calculationBasis: EnergyConsumptionCalculationBasis.ENERGY_COUNTER,
    calculationStart: StartDateMode.START_OF_SELECTED_DATE_RANGE
  },
  components: {
    ValveSensorTemperatureChart: {
      fields: ['inTemperature'],
      statistics: ['avg'],
      autoScaleYAxis: true,
      showLegends: true
    },
    ValveSensorHumidityChart: {
      fields: ['inHumidity'],
      statistics: ['avg'],
      autoScaleYAxis: true,
      showLegends: true
    },
    ValveSensorPressureChart: {
      fields: ['inPressure'],
      statistics: ['avg'],
      autoScaleYAxis: true,
      showLegends: true
    },
    ValveSensorPositionChart: {
      fields: ['setPoint', 'relativePosition'],
      statistics: ['avg'],
      autoScaleYAxis: true,
      showLegends: true,
      absolutePosition: false
    },
    ValveStats: {
      fields: ['inTemperature'],
      statistics: ['avg']
    }
  },

  setSettings: () => undefined,
  setPowerStatsSettings: () => undefined,
  setVoltageStatsSettings: () => undefined,
  setCurrentStatsSettings: () => undefined,
  setFrequencyStatsSettings: () => undefined,
  setPowerFactorStatsSettings: () => undefined,
  setEnergyConsumptionStatsSettings: () => undefined
})

export const useSettings = () => {
  return useContext(SettingsContext)
}

export const usePowerStatsSettings = () => {
  return useContext(SettingsContext).powerStatsSettings
}

export const useVoltageStatsSettings = () => {
  return useContext(SettingsContext).voltageStatsSettings
}

export const useCurrentStatsSettings = () => {
  return useContext(SettingsContext).currentStatsSettings
}

export const useFrequencyStatsSettings = () => {
  return useContext(SettingsContext).frequencyStatsSettings
}

export const usePowerFactorStatsSettings = () => {
  return useContext(SettingsContext).powerFactorStatsSettings
}

export const useEnergyConsumptionStatsSettings = () => {
  const { energyConsumptionStatsSettings, setEnergyConsumptionStatsSettings } =
    useContext(SettingsContext)

  return [energyConsumptionStatsSettings, setEnergyConsumptionStatsSettings]
}

export const useComponentSettings = () => {
  return useContext(SettingsContext).components
}

//
export const useValveSensorTemperatureChartSettings = () => {
  return useComponentSettings()?.ValveSensorTemperatureChart
}

interface SettingsProviderProps {
  children: ReactNode
}

export function SettingsProvider({ children }: SettingsProviderProps) {
  const getInitialSettings = () => {
    const {
      energyPriceType,
      energyPrice,
      emissionFactorMode,
      emissionFactorValue,
      generator,
      components,
      ...settings
    } = JSON.parse(window.localStorage.getItem('settings') ?? '{}')

    return {
      energyPriceType: energyPriceType ?? EnergyPriceType.SPOT,
      energyPrice: energyPrice ?? 0,
      emissionFactorMode: emissionFactorMode ?? EmissionFactorMode.REALTIME,
      emissionFactorValue: emissionFactorValue ?? 0,
      ...settings,
      generator: {
        powerUnit: generator?.powerUnit ?? PowerUnit.KILOVOLT_AMPERE,
        additionalLoadUnit: generator?.additionalLoadUnit ?? PowerUnit.KILOWATT
      },
      components: {
        ...components,
        ValveSensorTemperatureChart: {
          fields: components?.ValveSensorTemperatureChart?.fields ?? ['inTemperature'],
          statistics: components?.ValveSensorTemperatureChart?.statistics ?? ['avg'],
          autoScaleYAxis: components?.ValveSensorTemperatureChart?.autoScaleYAxis ?? true,
          showLegends: components?.ValveSensorTemperatureChart?.showLegends ?? true
        },
        ValveSensorHumidityChart: {
          fields: components?.ValveSensorHumidityChart?.fields ?? ['inHumidity'],
          statistics: components?.ValveSensorHumidityChart?.statistics ?? ['avg'],
          autoScaleYAxis: components?.ValveSensorHumidityChart?.autoScaleYAxis ?? true,
          showLegends: components?.ValveSensorHumidityChart?.showLegends ?? true
        },
        ValveSensorPressureChart: {
          fields: components?.ValveSensorPressureChart?.fields ?? ['inPressure'],
          statistics: components?.ValveSensorPressureChart?.statistics ?? ['avg'],
          autoScaleYAxis: components?.ValveSensorPressureChart?.autoScaleYAxis ?? true,
          showLegends: components?.ValveSensorPressureChart?.showLegends ?? true
        },
        ValveSensorPositionChart: {
          fields: components?.ValveSensorPositionChart?.fields ?? ['setPoint', 'relativePosition'],
          statistics: components?.ValveSensorPositionChart?.statistics ?? ['avg'],
          autoScaleYAxis: components?.ValveSensorPositionChart?.autoScaleYAxis ?? true,
          showLegends: components?.ValveSensorPositionChart?.showLegends ?? true,
          absolutePosition: components?.ValveSensorPositionChart?.absolutePosition ?? false
        },
        ValveStats: {
          fields: components?.ValveStats?.fields ?? ['inTemperature'],
          statistics: components?.ValveStats?.statistics ?? ['avg']
        }
      }
    }
  }

  const [settings, setSettings] = useState<Settings>(getInitialSettings())

  useEffect(() => {
    window.localStorage.setItem('settings', JSON.stringify(settings))
  }, [settings])

  /**
   * The power stats settings.
   */
  const powerStatsSettings: PowerStatsSettings = settings?.powerStatsSettings ?? {
    statistics: [SensorAggregateStatistic.AVG],
    fields: [
      SensorAggregateField.POWER,
      SensorAggregateField.POWER_1,
      SensorAggregateField.POWER_2,
      SensorAggregateField.POWER_3
    ],
    showTotalMaxPower: false,
    showPhaseMaxPower: true,
    zoomIn: false
  }

  /**
   * The voltage stats settings.
   */
  const voltageStatsSettings: VoltageStatsSettings = settings?.voltageStatsSettings ?? {
    statistics: [SensorAggregateStatistic.AVG],
    fields: [
      SensorAggregateField.VOLTAGE_1,
      SensorAggregateField.VOLTAGE_2,
      SensorAggregateField.VOLTAGE_3
    ],
    zoomIn: true
  }

  /**
   * The current stats settings.
   */
  const currentStatsSettings: CurrentStatsSettings = settings?.currentStatsSettings ?? {
    statistics: [SensorAggregateStatistic.AVG],
    fields: [
      SensorAggregateField.CURRENT_1,
      SensorAggregateField.CURRENT_2,
      SensorAggregateField.CURRENT_3
    ],
    showRatedCurrent: false,
    zoomIn: true
  }

  /**
   * The frequency stats settings.
   */
  const frequencyStatsSettings: FrequencyStatsSettings = settings?.frequencyStatsSettings ?? {
    statistics: [SensorAggregateStatistic.AVG],
    fields: [
      SensorAggregateField.FREQUENCY_FACTOR_1,
      SensorAggregateField.FREQUENCY_FACTOR_2,
      SensorAggregateField.FREQUENCY_FACTOR_3
    ]
  }

  /**
   * The power factor stats settings.
   */
  const powerFactorStatsSettings: PowerFactorStatsSettings = settings?.powerFactorStatsSettings ?? {
    statistics: [SensorAggregateStatistic.AVG],
    fields: [
      SensorAggregateField.POWER_FACTOR_1,
      SensorAggregateField.POWER_FACTOR_2,
      SensorAggregateField.POWER_FACTOR_3
    ]
  }

  /**
   * The energy consumption stats settings.
   */
  const energyConsumptionStatsSettings: EnergyConsumptionStatsSettings =
    settings?.energyConsumptionStatsSettings ?? {
      calculationMode: CalculationMode.CUMULATIVE,
      calculationBasis: EnergyConsumptionCalculationBasis.ENERGY_COUNTER,
      calculationStart: StartDateMode.START_OF_SELECTED_DATE_RANGE
    }

  /**
   *  The energy cost stats settings.
   */
  const energyCostStatsSettings: EnergyCostStatsSettings = {
    calculationMode: energyConsumptionStatsSettings.calculationMode,
    calculationBasis: energyConsumptionStatsSettings.calculationBasis,
    calculationStart: energyConsumptionStatsSettings.calculationStart
  }

  /**
   * The energy emission stats settings.
   */
  const energyEmissionStatsSettings: EnergyEmissionStatsSettings = {
    calculationMode: energyConsumptionStatsSettings.calculationMode,
    calculationBasis: energyConsumptionStatsSettings.calculationBasis,
    calculationStart: energyConsumptionStatsSettings.calculationStart
  }

  /**
   * Set the power stats settings.
   */
  const setPowerStatsSettings = (powerStatsSettings: PowerStatsSettings) => {
    powerStatsSettings = {
      ...powerStatsSettings,
      fields: powerStatsSettings.fields.filter((field) =>
        [
          SensorAggregateField.POWER,
          SensorAggregateField.POWER_1,
          SensorAggregateField.POWER_2,
          SensorAggregateField.POWER_3
        ].includes(field)
      )
    }

    setSettings({
      ...settings,
      powerStatsSettings
    })
  }

  /**
   * Set the voltage stats settings.
   */
  const setVoltageStatsSettings = (voltageStatsSettings: VoltageStatsSettings) => {
    voltageStatsSettings = {
      ...voltageStatsSettings,
      fields: voltageStatsSettings.fields.filter((field) =>
        [
          SensorAggregateField.VOLTAGE_1,
          SensorAggregateField.VOLTAGE_2,
          SensorAggregateField.VOLTAGE_3
        ].includes(field)
      )
    }

    setSettings({
      ...settings,
      voltageStatsSettings
    })
  }

  /**
   * Set the current stats settings.
   */
  const setCurrentStatsSettings = (currentStatsSettings: CurrentStatsSettings) => {
    currentStatsSettings = {
      ...currentStatsSettings,
      fields: currentStatsSettings.fields.filter((field) =>
        [
          SensorAggregateField.CURRENT_1,
          SensorAggregateField.CURRENT_2,
          SensorAggregateField.CURRENT_3
        ].includes(field)
      )
    }

    setSettings({
      ...settings,
      currentStatsSettings
    })
  }

  /**
   * Set the frequency stats settings.
   */
  const setFrequencyStatsSettings = (frequencyStatsSettings: FrequencyStatsSettings) => {
    frequencyStatsSettings = {
      ...frequencyStatsSettings,
      fields: frequencyStatsSettings.fields.filter((field) =>
        [
          SensorAggregateField.FREQUENCY_FACTOR_1,
          SensorAggregateField.FREQUENCY_FACTOR_2,
          SensorAggregateField.FREQUENCY_FACTOR_3
        ].includes(field)
      )
    }

    setSettings({
      ...settings,
      frequencyStatsSettings
    })
  }

  /**
   * Set the power factor stats settings.
   */
  const setPowerFactorStatsSettings = (powerFactorStatsSettings: PowerFactorStatsSettings) => {
    powerFactorStatsSettings = {
      ...powerFactorStatsSettings,
      fields: powerFactorStatsSettings.fields.filter((field) =>
        [
          SensorAggregateField.POWER_FACTOR_1,
          SensorAggregateField.POWER_FACTOR_2,
          SensorAggregateField.POWER_FACTOR_3
        ].includes(field)
      )
    }

    setSettings({
      ...settings,
      powerFactorStatsSettings
    })
  }

  /**
   * Set the energy stats settings.
   */
  const setEnergyConsumptionStatsSettings = (
    energyConsumptionStatsSettings: EnergyConsumptionStatsSettings
  ) => {
    setSettings({
      ...settings,
      energyConsumptionStatsSettings
    })
  }

  return (
    <SettingsContext.Provider
      value={{
        settings,
        powerStatsSettings,
        voltageStatsSettings,
        currentStatsSettings,
        frequencyStatsSettings,
        powerFactorStatsSettings,
        energyConsumptionStatsSettings,
        energyCostStatsSettings,
        energyEmissionStatsSettings,
        setSettings,
        setPowerStatsSettings,
        setVoltageStatsSettings,
        setCurrentStatsSettings,
        setFrequencyStatsSettings,
        setPowerFactorStatsSettings,
        setEnergyConsumptionStatsSettings
      }}
    >
      {children}
    </SettingsContext.Provider>
  )
}
